/* eslint-disable @mizdra/layout-shift/require-size-attributes */
import React, { useEffect } from 'react';
import { bool, number, string, shape as shapeType } from 'prop-types';
import {
  arrayOf,
  customType,
  fragment,
  params,
  shape,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  Button,
  Typography,
  Card,
  CardBody,
  CardMedia,
  CardTitle
} from '@one-thd/sui-atomic-components';
import { SpotlightB2b } from './SpotlightB2b';
import { publish } from './utils/publisher';

const Spotlight = ({
  componentId, componentClass, componentPosition, showDescription, useB2bStyles, proAnalyticsData
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('spotlight.ready');
  }, []);

  const { data } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass
    }
  });

  const impressionProviderContextData = useImpression({
    data: {
      id: componentId,
      name: Spotlight.displayName,
      component: Spotlight.displayName,
      position: componentPosition,
      type: 'content'
    }
  });

  const dataComponent = data?.component;

  let {
    cta,
    description,
    link,
    title,
    proAnalyticsCampaign,
    proAnalyticsComponent
  } = dataComponent || {};

  const {
    enterpriseContentModel,
    image: dataComponentImage,
    previewImage: dataComponentPreviewImage
  } = dataComponent || {};

  let {
    url: imageUrl,
  } = dataComponentImage?.damDownloadedContent || {};

  if (dataComponentPreviewImage) {

    const damDownloadedContentImage = dataComponentPreviewImage.damDownloadedContent;
    const damContentSelectorImage = dataComponentPreviewImage.damContentSelector?.assetData?.[0];

    if (damDownloadedContentImage?.url) {
      imageUrl = damDownloadedContentImage?.url;
    } else {
      imageUrl = damContentSelectorImage?.selectedImageUrl || imageUrl;
    }
  }

  if (enterpriseContentModel) {
    cta = enterpriseContentModel.cta;
    description = enterpriseContentModel.subhead;
    link = enterpriseContentModel.link_cta;
    title = enterpriseContentModel.headline_LessThan50Char;
    imageUrl = enterpriseContentModel.image_lifestyle?.damDownloadedContent?.url || imageUrl;
  }
  const handleContentClick = () => {
    if (proAnalyticsData.isB2B) {
      // Input Data for widget should be collected from component data
      publish({
        widgetId: componentId,
        widgetTemplates: proAnalyticsData.templates,
        widgetCampaign: proAnalyticsCampaign,
        widgetComponent: proAnalyticsComponent,
      });
    }
  };
  if (useB2bStyles) {
    return (
      <SpotlightB2b
        componentId={componentId}
        componentClass={componentClass}
        componentPosition={componentPosition}
        proAnalyticsData={proAnalyticsData}
      />
    );
  }

  return (
    <div
      data-component-id={componentId}
      id={`${componentClass}-${componentPosition}-${componentId}`}
      data-component="Spotlight"
      data-component-name="Spotlight"
      data-component-position={componentPosition}
      className="spotlight--grid sui-grid sui-h-full"
      ref={impressionProviderContextData.ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={impressionProviderContextData.clickID}
    >
      <Card>
        <CardMedia
          src={imageUrl}
          aspect="wide"
          alt={`Image for ${title}`}
          title={`Image for ${title}`}
        />
        <CardTitle header={title} />
        <CardBody>
          {showDescription && description
            && (
              <Typography height="normal" weight="regular" variant="body-base" color="primary">
                {description}
              </Typography>
            )}
        </CardBody>
        <Button href={link} variant="secondary" onClick={handleContentClick}>{cta || ''}</Button>
      </Card>
    </div>
  );
};

Spotlight.propTypes = {
  componentId: string.isRequired,
  componentPosition: number,
  componentClass: string,
  showDescription: bool,
  useB2bStyles: bool,
  proAnalyticsData: shapeType({
    isB2B: bool,
    templates: string
  })
};

Spotlight.defaultProps = {
  componentPosition: 1,
  componentClass: '',
  showDescription: false,
  useB2bStyles: false,
  proAnalyticsData: {}
};

Spotlight.displayName = 'Spotlight';

Spotlight.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['Spotlight']).isRequired(),
  }).shape({
    Spotlight: fragment().shape({
      id: stringType(),
      title: stringType(),
      cta: stringType(),
      link: stringType(),
      componentName: stringType(),
      description: stringType(),
      proAnalyticsCampaign: stringType(),
      proAnalyticsComponent: stringType(),
      image: shape({
        damDownloadedContent: shape({
          url: stringType()
        }),
      }),
      previewImage: shape({
        damContentSelector: shape({
          assetData: arrayOf(shape({
            selectedImageUrl: stringType()
          }))
        }),
        damDownloadedContent: shape({
          url: stringType()
        })
      }),
      enterpriseContentModel: shape({
        link_cta: stringType(),
        image_lifestyle: shape({
          damDownloadedContent: shape({
            url: stringType()
          }),
        }),
        headline_LessThan50Char: stringType(),
        subhead: stringType(),
        cta: stringType()
      })
    })
  })
};

export { Spotlight };
